import {AfterViewInit, Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {TicketsService} from '../../../../services/tickets/tickets.service';
import {RouteService} from '../../../../services/route/route.service';
import {finalize} from 'rxjs/operators';
import {OperationDetail, TicketDetail, TicketStatus, TransactionCustomer} from '../../../../generated/sales-terminal-client';
import {DataTable, DataTableRowItemType} from '../../../../components/data-table/data-table.interfaces';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {UtilService} from '../../../../services/util/util.service';
import {Params} from '@angular/router';
import {AuthService} from '../../../../services/auth/auth.service';

@Component({
  selector: 'app-ticket',
  templateUrl: './ticket.component.html',
  styleUrl: './ticket.component.scss'
})
export class TicketComponent implements OnInit, AfterViewInit {
  @ViewChild('statusTemplate') public statusTemplate: TemplateRef<any>;
  @ViewChild('opTimeTemplate') public opTimeTemplate: TemplateRef<any>;
  @ViewChild('transactionIdTemplate') public transactionIdTemplate: TemplateRef<any>;
  @ViewChild('invoiceNumberTemplate') public invoiceNumberTemplate: TemplateRef<any>;
  @ViewChild('ticketTypeTemplate') public ticketTypeTemplate: TemplateRef<any>;
  @ViewChild('priceTemplate') public priceTemplate: TemplateRef<any>;
  @ViewChild('feesTemplate') public feesTemplate: TemplateRef<any>;
  @ViewChild('totalTemplate') public totalTemplate: TemplateRef<any>;
  @ViewChild('customerTemplate') public customerTemplate: TemplateRef<any>;
  @ViewChild('deliveryEmailTemplate') public deliveryEmailTemplate: TemplateRef<any>;
  @ViewChild('salespointTemplate') public salespointTemplate: TemplateRef<any>;
  @ViewChild('sellerTemplate') public sellerTemplate: TemplateRef<any>;
  @ViewChild('qrCodeDialogTemplate') public qrCodeDialogTemplate: TemplateRef<any>;

  public isLoading: boolean;
  public ticket: TicketDetail;
  public currentOperation: OperationDetail;
  public dataTableConfig: DataTable;

  constructor(private ticketsService: TicketsService,
              private routeService: RouteService,
              public dialog: MatDialog,
              public utilService: UtilService,
              public authService: AuthService) {}

  public ngOnInit(): void {
    this.isLoading = true;
    this.ticketsService.getTicket(this.routeService.params['id']).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((ticket: TicketDetail) => {
      this.ticket = ticket;
      this.currentOperation = this.ticket.operations.find(op => op.id === this.ticket.currentOperationId);
    });
  }
  public ngAfterViewInit(): void {
    this.dataTableConfig = {
      header: {
        items: [
          {
            key: 'operation',
            format: 'general.api_ticket_tool_status',
            cssClass: 'break-word'
          },
          {
            key: 'operation_time',
            format: 'general.api_ticket_tool_operation_time',
            cssClass: 'break-word'
          },
          {
            key: 'transaction_id',
            format: 'general.api_ticket_tool_transaction_id',
            cssClass: 'break-word'
          },
          {
            key: 'invoice_number',
            format: 'general.api_ticket_tool_invoice_number',
            cssClass: 'break-word'
          },
          {
            key: 'ticket_type',
            format: 'general.api_ticket_tool_ticket_type',
            cssClass: 'break-word'
          },
          {
            key: 'price',
            format: 'general.api_ticket_tool_price',
            cssClass: 'align-right break-word',
            showCurrency: true
          },
          {
            key: 'fees',
            format: 'general.api_ticket_tool_fees',
            cssClass: 'align-right break-word',
            children: [
              {
                key: 'fee_value',
                format: 'general.api_ticket_tool_fee_value',
                cssClass: 'align-right subtitle break-word',
                showCurrency: true
              }
            ]
          },
          {
            key: 'total',
            format: 'general.api_ticket_tool_total',
            cssClass: 'align-right break-word',
            showCurrency: true
          },
          {
            key: 'customer',
            format: 'general.api_ticket_tool_customer',
            cssClass: 'break-word',
            children: [
              {
                key: 'delivery_email',
                format: 'general.api_ticket_tool_delivery_email',
                cssClass: 'subtitle break-word'
              }
            ]
          },
          {
            key: 'salespoint',
            format: 'general.api_ticket_tool_salespoint',
            cssClass: 'break-word',
            children: [
              {
                key: 'seller',
                format: 'general.api_ticket_tool_seller',
                cssClass: 'subtitle break-word'
              }
            ]
          }
        ]
      },
      body: {
        items: [
          {
            key: 'operation',
            mobileHeaderItem: true,
            type: DataTableRowItemType.Template,
            template: this.statusTemplate
          },
          {
            key: 'operation_time',
            type: DataTableRowItemType.Template,
            template: this.opTimeTemplate
          },
          {
            key: 'transaction_id',
            type: DataTableRowItemType.Template,
            template: this.transactionIdTemplate
          },
          {
            key: 'invoice_number',
            type: DataTableRowItemType.Template,
            template: this.invoiceNumberTemplate
          },
          {
            key: 'ticket_type',
            type: DataTableRowItemType.Template,
            template: this.ticketTypeTemplate
          },
          {
            key: 'price',
            cssClass: 'align-right',
            type: DataTableRowItemType.Template,
            template: this.priceTemplate
          },
          {
            key: 'fees',
            type: DataTableRowItemType.Template,
            template: this.feesTemplate,
            children: [
              {
                key: 'fee_value'
              }
            ]
          },
          {
            key: 'total',
            cssClass: 'align-right',
            type: DataTableRowItemType.Template,
            template: this.totalTemplate
          },
          {
            key: 'customer',
            type: DataTableRowItemType.Template,
            template: this.customerTemplate,
            children: [
              {
                key: 'delivery_email',
                cssClass: 'subtitle',
                type: DataTableRowItemType.Template,
                template: this.deliveryEmailTemplate
              }
            ]
          },
          {
            key: 'salespoint',
            type: DataTableRowItemType.Template,
            template: this.salespointTemplate,
            children: [
              {
                key: 'seller',
                cssClass: 'subtitle',
                type: DataTableRowItemType.Template,
                template: this.sellerTemplate
              }
            ]
          }
        ]
      }
    }
  }

  public openTransaction(transactionId: number): void {
    const queryParams: Params = {ticketid: this.ticket.id};
    this.routeService.navigate('app/sales-terminal/transactions/view/' + transactionId, {queryParams});
  }
  public openCustomer(customerUuid: string): void {
    this.routeService.navigate('app/admin/customers/view/' + customerUuid);
  }

  public showQR(): void {
    this.dialog.open(this.qrCodeDialogTemplate, {width: '40%'});
  }

  public toList(): void {
    this.routeService.navigate('app/sales-terminal/tickets');
  }

  public canNavigateToCustomer(customerUuid: string): boolean {
    return !!customerUuid?.length && this.authService.user.isSuperCentre && this.authService.hasCustomersAccessPrivileges;
  }

  protected readonly TicketStatus = TicketStatus;
}
